import { useKitData } from "@/data/use-kits-data";
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useCreateAssistMessageAction } from "../../action/use-create-assist-message-action";
import { useGroupUuid } from "../../hooks/use-group-uuid";
import { useKitUuid } from "../../hooks/use-kit-uuid";
import { useMentionSections } from "../../lib/mentions";
import { cn } from "../../lib/utils";
import { AIChatInput } from "../AIChatInput/AIChatInput";
import { useExpertSelection } from "../Expert/selection/ExpertSelectionContext";
import { ChatFileManager } from "../file/ChatFileManager/ChatFileManager";
import { ChatFileManagerAttachmentButton } from "../file/ChatFileManager/ChatFileManagerAttachmentButton";
import { useChatFileManager } from "../file/ChatFileManager/ChatFileManagerContext";
import { useAssistContext } from "./AssistContext";
import { SingleExpertSkillsSection } from "./skill/SingleExpertSkillsSection";

type AssistBodyFooterProps = {
  variant?: "default" | "minimal";
  onNewChat?: (uuid: string) => void;
};

export const AssistBodyFooter: React.FC<AssistBodyFooterProps> = ({
  variant = "default",
  onNewChat,
}) => {
  const { selectedExpertUuids, selectExperts, kitExperts } =
    useExpertSelection();
  const {
    activeChatUuid,
    setSelectedSections,
    userPrompt,
    setUserPrompt,
    messageStage,
    capabilities,
    setCapabilities,
    modelProfileUuid,
    setModelProfileUuid,
    activeChat,
  } = useAssistContext();

  useMentionSections(userPrompt, setSelectedSections);

  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const { createMessage, cancelMessage } = useCreateAssistMessageAction();
  const { kit } = useKitData(groupUuid, kitUuid);
  const previousActiveChatUuid = useRef<string | null>(null);
  const hasAutoSelectedSoleExpert = useRef(false);
  const isSubmittingRef = useRef(false);
  const { selectedLibraryFiles, clearFileManager } = useChatFileManager();

  const chatExpertUuids = activeChat?.summarizedChats.length
    ? activeChat.summarizedChats.map((sc) => sc.expertUuid)
    : activeChat?.expertUuid
      ? [activeChat.expertUuid]
      : [];

  const displayInput =
    activeChat === null ||
    chatExpertUuids.every((expertUuid) =>
      kitExperts.some((ke) => ke.uuid === expertUuid),
    );

  useEffect(() => {
    if (activeChat && activeChat.uuid !== previousActiveChatUuid.current) {
      previousActiveChatUuid.current = activeChat.uuid;
      hasAutoSelectedSoleExpert.current = false;

      const chatExperts = activeChat.expertUuid
        ? [activeChat.expertUuid]
        : activeChat.summarizedChats.map((chat) => chat.expertUuid);

      if (chatExperts.length > 0) {
        selectExperts(new Set(chatExperts));
      }

      return;
    }

    if (selectedExpertUuids.size > 0) {
      hasAutoSelectedSoleExpert.current = true;
      return;
    }

    if (!hasAutoSelectedSoleExpert.current && kitExperts.length === 1) {
      hasAutoSelectedSoleExpert.current = true;
      selectExperts(new Set([kitExperts[0].uuid]));
    }
  }, [activeChat, kitExperts, selectedExpertUuids]);

  async function handleSubmit(params: {
    chatName?: string;
    skillUuid?: string;
    prompt?: string;
    capabilityOverrides?: Parameters<
      typeof createMessage
    >[0]["capabilityOverrides"];
  }) {
    if (!selectedExpertUuids.size || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    try {
      const { chatUuid } = await createMessage({
        expertUuids: selectedExpertUuids,
        skillUuid: params.skillUuid,
        capabilityOverrides: params.capabilityOverrides,
        fileUuids: selectedLibraryFiles.map((f) => f.uuid),
      });

      if (chatUuid !== activeChatUuid) {
        onNewChat?.(chatUuid);
      }

      clearFileManager();
    } catch (error) {
      console.error(error);
      toast.error("Failed to send message");
    } finally {
      isSubmittingRef.current = false;
    }
  }

  const handleCancelMessage = () => {
    cancelMessage();
  };

  const filePickerElement =
    groupUuid && kitUuid ? (
      <ChatFileManagerAttachmentButton
        library={true}
        disabled={messageStage !== "idle"}
      />
    ) : undefined;

  if (!kit) {
    return null;
  }

  return (
    <div
      className={cn("flex shrink-0 flex-col", {
        "pr-4 pb-4 pl-4": variant === "default",
      })}
    >
      {displayInput && selectedExpertUuids.size === 1 && (
        <SingleExpertSkillsSection
          key={selectedExpertUuids.values().next().value}
          expertUuid={selectedExpertUuids.values().next().value!}
          onSelect={(params) => handleSubmit(params)}
        />
      )}

      {displayInput && (
        <div className="flex flex-col gap-2">
          {/*
          ChatFileManagerProvider:
          - ui/src/components/Assist/AssistBody.tsx
          - ui/src/pages/kit/ConversationPage.tsx
          */}
          <ChatFileManager goal="upload" />

          <AIChatInput
            onSubmit={(prompt) => handleSubmit({ prompt })}
            onCancel={
              messageStage === "uploading" ? undefined : handleCancelMessage
            }
            isPending={messageStage !== "idle"}
            filePickerElement={filePickerElement}
            value={userPrompt}
            onChange={setUserPrompt}
            capabilities={capabilities}
            onCapabilitiesChange={setCapabilities}
            modelProfileUuid={modelProfileUuid}
            onModelProfileUuidChange={setModelProfileUuid}
            expectExpertSelection
          />
        </div>
      )}
    </div>
  );
};
